/*
 * Strings for the spare parts register (features/cmmsSpareParts).
 *
 * The movement type labels are NOT here — they live in features/cmms/cmmsMessages.js with the
 * other enum label helpers, because the work order's material row will name them too.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms.spareparts";

export default defineMessages({
  // page
  pageTitle: { id: `${scope}.page.title`, defaultMessage: "Spare parts" },
  pageSubtitle: {
    id: `${scope}.page.subtitle`,
    defaultMessage: "The maintenance store. Stock is the sum of its movements less what work orders have booked.",
  },
  addSparePart: { id: `${scope}.page.addSparePart`, defaultMessage: "Add spare part" },

  // statistic tile. Upper-cased by CSS, not by the message — shouting at translators forces
  // them to guess whether the caps mean anything, and some locales have no upper case at all.
  //
  // The id still says reorderPoint; the words say min. See the note above the level pair below.
  statBelowReorderPoint: { id: `${scope}.stat.belowReorderPoint`, defaultMessage: "Below min" },

  // filters. The search label names the fields it covers, like every other search box in the
  // app — a bare "Search" leaves someone guessing whether the location is included.
  // "article number" covers ours and the supplier's alike, and "name" covers the second name
  // line — naming all six fields would be a label nobody finishes reading.
  filterSearch: {
    id: `${scope}.filter.search`,
    defaultMessage: "Search by part number, article number, name, description or location",
  },
  filterShowRetired: { id: `${scope}.filter.showRetired`, defaultMessage: "Show inactive" },
  filterBelowReorderPoint: { id: `${scope}.filter.belowReorderPoint`, defaultMessage: "Below min only" },
  filterClear: { id: `${scope}.filter.clear`, defaultMessage: "Clear filters" },

  // grid columns. The supplier's article number and name2 are abbreviated because they are the
  // widest headers on the grid and the narrowest thing they label is a shelf code.
  columnPartNumber: { id: `${scope}.column.partNumber`, defaultMessage: "Part #" },
  columnSupplierArticleNumber: {
    id: `${scope}.column.supplierArticleNumber`,
    defaultMessage: "Supplier article no.",
  },
  columnName: { id: `${scope}.column.name`, defaultMessage: "Name" },
  columnName2: { id: `${scope}.column.name2`, defaultMessage: "Name 2" },
  columnSparePartType: { id: `${scope}.column.sparePartType`, defaultMessage: "Type" },
  columnStorageLocation: { id: `${scope}.column.storageLocation`, defaultMessage: "Location" },
  columnUnit: { id: `${scope}.column.unit`, defaultMessage: "Unit" },
  columnUnitCost: { id: `${scope}.column.unitCost`, defaultMessage: "Unit cost" },
  columnQuantityOnHand: { id: `${scope}.column.quantityOnHand`, defaultMessage: "On hand" },
  /*
   * The level pair, and why one of them is called reorderPoint in the code and Min on screen.
   *
   * The workshop asked for "min och max" on the register, and phase 15 Q2 asked which of two
   * readings that was: order-at plus order-up-to, two numbers, or point plus floor plus ceiling,
   * three. Answered 2026-08-28 — two. SparePart.ReorderPoint has held order-at since the register
   * was built, so it *is* the min, and only the ceiling was new (BE-15.12). There is no
   * MinStockLevel missing anywhere; this is the whole pair.
   *
   * The ids stay as they are. Renaming them would orphan every translation and reset the saved
   * column layouts, which are keyed by field name — and a key name is not text anyone reads.
   */
  columnReorderPoint: { id: `${scope}.column.reorderPoint`, defaultMessage: "Min" },
  columnMaxStockLevel: { id: `${scope}.column.maxStockLevel`, defaultMessage: "Max" },
  columnActive: { id: `${scope}.column.active`, defaultMessage: "Status" },
  statusActive: { id: `${scope}.status.active`, defaultMessage: "Active" },
  statusRetired: { id: `${scope}.status.retired`, defaultMessage: "Inactive" },

  // row actions
  actionEdit: { id: `${scope}.action.edit`, defaultMessage: "Edit spare part" },
  actionStock: { id: `${scope}.action.stock`, defaultMessage: "Stock and movements" },
  actionRetire: { id: `${scope}.action.retire`, defaultMessage: "Deactivate spare part" },
  actionRestore: { id: `${scope}.action.restore`, defaultMessage: "Restore spare part" },

  // empty + error states
  loadFailed: { id: `${scope}.error.loadFailed`, defaultMessage: "Could not load the spare parts register" },
  emptyTitle: { id: `${scope}.empty.title`, defaultMessage: "No spare parts yet" },
  emptyHint: {
    id: `${scope}.empty.hint`,
    defaultMessage: "Add the parts the maintenance store holds, then book what arrives and what is used.",
  },
  emptyFilteredTitle: { id: `${scope}.empty.filteredTitle`, defaultMessage: "No spare parts match the filters" },
  emptyFilteredHint: { id: `${scope}.empty.filteredHint`, defaultMessage: "Try clearing the filters." },

  // add / edit dialog
  formAddTitle: { id: `${scope}.form.addTitle`, defaultMessage: "Add spare part" },
  formEditTitle: { id: `${scope}.form.editTitle`, defaultMessage: "Edit spare part" },
  fieldPartNumber: { id: `${scope}.field.partNumber`, defaultMessage: "Part number" },
  fieldName: { id: `${scope}.field.name`, defaultMessage: "Name" },
  fieldName2: { id: `${scope}.field.name2`, defaultMessage: "Name 2" },
  fieldSupplierArticleNumber: {
    id: `${scope}.field.supplierArticleNumber`,
    defaultMessage: "Supplier article number",
  },
  fieldDescription: { id: `${scope}.field.description`, defaultMessage: "Description" },
  fieldUnit: { id: `${scope}.field.unit`, defaultMessage: "Unit" },
  /*
   * The two dropdowns, which is why each carries six strings where a text box carries one: what
   * "none" is called, how to search it, what a retired option says about itself, and what to show
   * when the list is empty or would not load.
   *
   * fieldSparePartTypeSearch repeats fieldStorageLocationSearch word for word. Kept separate all
   * the same — a six-key set with one key borrowed from the field above it reads as an oversight,
   * and the day one of the two placeholders needs to say something the other does not, the sharing
   * would have to be undone anyway.
   */
  fieldSparePartType: { id: `${scope}.field.sparePartType`, defaultMessage: "Type" },
  fieldSparePartTypeNone: { id: `${scope}.field.sparePartTypeNone`, defaultMessage: "No type" },
  fieldSparePartTypeSearch: { id: `${scope}.field.sparePartTypeSearch`, defaultMessage: "Search…" },
  // Shown for a part of a kind that has since been retired. It stays selected — the server does
  // not check IsActive when a part is saved — and saying so is what stops it reading as an
  // ordinary choice.
  fieldSparePartTypeInactiveOption: {
    id: `${scope}.field.sparePartTypeInactiveOption`,
    defaultMessage: "{name} (inactive)",
  },
  fieldSparePartTypeEmpty: {
    id: `${scope}.field.sparePartTypeEmpty`,
    defaultMessage: "No spare part types yet. They are added under Maintenance → Settings.",
  },
  fieldSparePartTypeLoadFailed: {
    id: `${scope}.field.sparePartTypeLoadFailed`,
    defaultMessage: "Could not load the spare part types. The part can be saved without one.",
  },
  fieldStorageLocation: { id: `${scope}.field.storageLocation`, defaultMessage: "Storage location" },
  fieldStorageLocationNone: { id: `${scope}.field.storageLocationNone`, defaultMessage: "No location" },
  fieldStorageLocationSearch: { id: `${scope}.field.storageLocationSearch`, defaultMessage: "Search…" },
  // Shown for a part standing on a shelf that has since been retired. It stays selected — the
  // server allows that — and saying so is what stops it reading as an ordinary choice.
  fieldStorageLocationInactiveOption: {
    id: `${scope}.field.storageLocationInactiveOption`,
    defaultMessage: "{name} (inactive)",
  },
  fieldStorageLocationEmpty: {
    id: `${scope}.field.storageLocationEmpty`,
    defaultMessage: "No storage locations yet. They are added under Maintenance → Settings.",
  },
  fieldStorageLocationLoadFailed: {
    id: `${scope}.field.storageLocationLoadFailed`,
    defaultMessage: "Could not load the storage locations. The part can be saved without one.",
  },
  fieldUnitCost: { id: `${scope}.field.unitCost`, defaultMessage: "Unit cost" },
  // The form spells the pair out where the grid cannot: two narrow columns can be Min and Max,
  // but the box someone types a number into should say what the number is for.
  fieldReorderPoint: { id: `${scope}.field.reorderPoint`, defaultMessage: "Min (order at)" },
  fieldReorderPointHint: {
    id: `${scope}.field.reorderPointHint`,
    defaultMessage: "Flagged at this level, not only below it. Leave empty for no alert.",
  },
  fieldMaxStockLevel: { id: `${scope}.field.maxStockLevel`, defaultMessage: "Max (order up to)" },
  fieldMaxStockLevelHint: {
    id: `${scope}.field.maxStockLevelHint`,
    defaultMessage: "The level a replenishment fills back to. Leave empty for no ceiling.",
  },

  // picker — one option line in the work order's material dialog. The quantity is in
  // the label because choosing is when it matters.
  pickerOptionLabel: {
    id: `${scope}.picker.optionLabel`,
    defaultMessage: "{partNumber} · {name} — {quantity} {unit}",
  },
  pickerSummary: {
    id: `${scope}.picker.summary`,
    defaultMessage: "{name} · {unit}",
  },
  pickerSummaryWithCost: {
    id: `${scope}.picker.summaryWithCost`,
    defaultMessage: "{name} · {unit} · {unitCost}",
  },

  // stock drawer
  stockTitle: { id: `${scope}.stock.title`, defaultMessage: "Stock" },
  stockOnHand: { id: `${scope}.stock.onHand`, defaultMessage: "On hand" },
  stockReorderPoint: { id: `${scope}.stock.reorderPoint`, defaultMessage: "Min" },
  stockMaxStockLevel: { id: `${scope}.stock.maxStockLevel`, defaultMessage: "Max" },
  // Serves both halves of the pair. One string for "no level here" beats two identical ones for
  // translators to keep in step; the id names the field it was written for, not the only one it
  // answers for.
  stockNoReorderPoint: { id: `${scope}.stock.noReorderPoint`, defaultMessage: "Not set" },
  stockActionReceipt: { id: `${scope}.stock.action.receipt`, defaultMessage: "Goods received" },
  stockActionAdjust: { id: `${scope}.stock.action.adjust`, defaultMessage: "Adjust" },
  stockActionStocktake: { id: `${scope}.stock.action.stocktake`, defaultMessage: "Stocktake" },
  stockLedgerTitle: { id: `${scope}.stock.ledgerTitle`, defaultMessage: "Movements" },
  stockLedgerEmpty: { id: `${scope}.stock.ledgerEmpty`, defaultMessage: "Nothing booked yet" },
  stockLedgerEmptyHint: {
    id: `${scope}.stock.ledgerEmptyHint`,
    defaultMessage: "Goods received, adjustments and stocktakes appear here. What work orders use is booked on the order.",
  },
  stockLedgerLoadFailed: { id: `${scope}.stock.ledgerLoadFailed`, defaultMessage: "Could not load the movements" },
  stockRetired: { id: `${scope}.stock.retired`, defaultMessage: "This part is inactive. Nothing further can be booked on it." },
  stockMovedBy: { id: `${scope}.stock.movedBy`, defaultMessage: "by {user}" },

  // movement dialog
  movementTitle: { id: `${scope}.movement.title`, defaultMessage: "Book a movement" },
  movementType: { id: `${scope}.movement.type`, defaultMessage: "Type" },
  movementDirection: { id: `${scope}.movement.direction`, defaultMessage: "Direction" },
  movementDirectionIncrease: { id: `${scope}.movement.direction.increase`, defaultMessage: "Increase" },
  movementDirectionDecrease: { id: `${scope}.movement.direction.decrease`, defaultMessage: "Decrease" },
  movementQuantity: { id: `${scope}.movement.quantity`, defaultMessage: "Quantity" },
  movementReason: { id: `${scope}.movement.reason`, defaultMessage: "Reason" },
  movementReasonRequired: { id: `${scope}.movement.reasonRequired`, defaultMessage: "Reason (required)" },
  movementSaveFailed: { id: `${scope}.movement.saveFailed`, defaultMessage: "Could not book the movement" },

  // stocktake dialog
  stocktakeTitle: { id: `${scope}.stocktake.title`, defaultMessage: "Stocktake" },
  stocktakeBookBalance: { id: `${scope}.stocktake.bookBalance`, defaultMessage: "On hand per the ledger" },
  stocktakeCounted: { id: `${scope}.stocktake.counted`, defaultMessage: "Counted quantity" },
  stocktakeDifference: { id: `${scope}.stocktake.difference`, defaultMessage: "Difference" },
  stocktakeHint: {
    id: `${scope}.stocktake.hint`,
    defaultMessage: "Enter what is actually on the shelf. The difference is worked out when the count is filed.",
  },
  stocktakeSaveFailed: { id: `${scope}.stocktake.saveFailed`, defaultMessage: "Could not file the count" },

  // deactivate confirmation. The key names still say retire: the words on screen were changed
  // later, not the ids, because a key name is not text anyone reads.
  retireTitle: { id: `${scope}.retire.title`, defaultMessage: "Deactivate spare part" },
  retireMessage: {
    id: `${scope}.retire.message`,
    defaultMessage: "Deactivate \"{name}\"? It stays in the register so booked history keeps its subject, but nothing further can be booked on it.",
  },
  retireConfirm: { id: `${scope}.retire.confirm`, defaultMessage: "Deactivate" },

  // restore confirmation. Says what comes back rather than "are you sure" — the
  // consequence a manager cares about is that the part can be booked on again.
  restoreTitle: { id: `${scope}.restore.title`, defaultMessage: "Restore spare part" },
  restoreMessage: {
    id: `${scope}.restore.message`,
    defaultMessage: "Restore \"{name}\"? It goes back into the register and can be booked on and stocked again.",
  },
  restoreConfirm: { id: `${scope}.restore.confirm`, defaultMessage: "Restore" },

  // validation
  validationPartNumberRequired: { id: `${scope}.validation.partNumberRequired`, defaultMessage: "Part number is required" },
  validationNameRequired: { id: `${scope}.validation.nameRequired`, defaultMessage: "Name is required" },
  validationUnitRequired: { id: `${scope}.validation.unitRequired`, defaultMessage: "Unit is required" },
  validationQuantityPositive: { id: `${scope}.validation.quantityPositive`, defaultMessage: "Quantity must be greater than 0" },
  validationReasonRequired: { id: `${scope}.validation.reasonRequired`, defaultMessage: "An adjustment must say why" },
  validationCountedNotNegative: { id: `${scope}.validation.countedNotNegative`, defaultMessage: "A counted quantity cannot be negative" },
  validationNumber: { id: `${scope}.validation.number`, defaultMessage: "Must be a number" },
  // Named in the words on the labels, not in the field names behind them. The server says
  // "MaxStockLevel 4 is below ReorderPoint 10", which is true and no help to someone looking at
  // two boxes marked Min and Max.
  validationMaxBelowMin: { id: `${scope}.validation.maxBelowMin`, defaultMessage: "Max cannot be lower than min" },

  // toasts
  toastCreated: { id: `${scope}.toast.created`, defaultMessage: "Spare part created" },
  toastUpdated: { id: `${scope}.toast.updated`, defaultMessage: "Spare part updated" },
  toastRetired: { id: `${scope}.toast.retired`, defaultMessage: "Spare part deactivated" },
  toastRestored: { id: `${scope}.toast.restored`, defaultMessage: "Spare part restored" },
  toastMovementBooked: { id: `${scope}.toast.movementBooked`, defaultMessage: "Movement booked" },
  toastStocktakeFiled: { id: `${scope}.toast.stocktakeFiled`, defaultMessage: "Counted — {quantity} on hand" },
  saveFailed: { id: `${scope}.error.saveFailed`, defaultMessage: "Could not save the spare part" },
  retireFailed: { id: `${scope}.error.retireFailed`, defaultMessage: "Could not deactivate the spare part" },
  restoreFailed: { id: `${scope}.error.restoreFailed`, defaultMessage: "Could not restore the spare part" },
});
